import { useErrorStore } from '@/stores';
import type { Error } from '@/types/models/error.d';
import { HTTP_STATUS } from '@/constants/common';
import {
  ApiError,
  ApiOrganizationNotSelectedError,
  ApiRequestCanceledError,
  ApiSophonError,
  ApiSophonMfaRequiredError,
  ApiSophonSessionExpiredError,
} from '@/errors';
import { useI18n } from '#imports';
import { useAuth0 } from '@auth0/auth0-vue';
import { tryRecoverSession, beginRedirect, endRedirect } from '@/utils/auth/session-recovery';

export function useErrorHandler() {
  const { t } = useI18n();
  const errorStore = useErrorStore();
  const { loginWithRedirect } = useAuth0();
  const logger = useLogger();

  const errorMap = new Map([
    [
      HTTP_STATUS.STATUS_CODE.UNAUTHORIZED,
      { redirect: true, message: t('errors.view.unauth-err') },
    ],
    [HTTP_STATUS.STATUS_CODE.FORBIDDEN, { redirect: true, message: t('errors.view.unauth-err') }],
    [HTTP_STATUS.STATUS_CODE.BAD_REQUEST, { message: t('errors.view.bad-request-err') }],
    [HTTP_STATUS.STATUS_CODE.NOT_FOUND, { message: t('errors.view.not-found-err') }],
    [HTTP_STATUS.STATUS_CODE.CONFLICT, { message: t('errors.view.conflict-err') }],
    [
      HTTP_STATUS.STATUS_CODE.UNPROCESSABLE_ENTITY,
      { message: t('errors.view.unprocessable-entity-err') },
    ],
    [
      HTTP_STATUS.STATUS_CODE.INTERNAL_SERVER_ERROR,
      { message: t('errors.view.internal-server-err') },
    ],
  ]);

  const setApiErrorObject = (statusCode: number, errorCode?: string) => {
    const errorObject: Error = {};
    errorObject.message =
      errorMap.get(statusCode)?.message ?? `${t('errors.view.unknown-error')}(Code: ${errorCode})`;
    errorObject.showDialog = true;
    errorObject.status = String(statusCode);
    errorObject.redirect = !!errorMap.get(statusCode)?.redirect;
    errorStore.set(errorObject);
  };

  const handleError = async (error: unknown) => {
    logger.error({ error }, 'Error');

    if (error instanceof ApiRequestCanceledError) {
      return Promise.reject(error.message ?? 'Request Cancelled');
    }

    if (error instanceof ApiOrganizationNotSelectedError) {
      errorStore.set({
        message: t('errors.view.organization-not-selected-err'),
        showDialog: true,
        redirect: true,
      });
      return;
    }

    if (error instanceof ApiSophonError) {
      if (error instanceof ApiSophonMfaRequiredError) {
        if (!beginRedirect()) {
          // セッション復旧など他の経路が同一ページロードで既に loginWithRedirect() を
          // 進行中。並行して呼ぶと Auth0 の redirect transaction を上書きし合うため何もしない
          return;
        }
        try {
          await loginWithRedirect({
            authorizationParams: {
              acr_values: 'http://schemas.openid.net/pape/policies/2007/06/multi-factor',
            },
          });
        } catch (redirectError) {
          endRedirect();
          throw redirectError;
        }
        return;
      }

      if (error instanceof ApiSophonSessionExpiredError) {
        // SSOセッションが生きていれば /authorize への遷移だけで黙って復帰する。
        // 復帰できない場合のみ従来の401ダイアログ相当を表示する
        tryRecoverSession(() =>
          setApiErrorObject(
            error.statusCode ?? HTTP_STATUS.STATUS_CODE.UNAUTHORIZED,
            error.errorCode,
          ),
        );
        return;
      }
    }

    if (error instanceof ApiError) {
      const statusCode = error.statusCode ?? HTTP_STATUS.STATUS_CODE.INTERNAL_SERVER_ERROR;
      setApiErrorObject(statusCode, error.errorCode);
    } else {
      const errorObject: Error = {};
      errorObject.message = t('errors.view.unknown-error');
      errorObject.showDialog = true;
      errorObject.status = String(HTTP_STATUS.STATUS_CODE.INTERNAL_SERVER_ERROR);
      errorStore.set(errorObject);
    }
  };

  const isAuthError = (error: unknown): boolean => {
    if (error instanceof ApiOrganizationNotSelectedError) {
      return true;
    }

    if (error instanceof ApiSophonError && error instanceof ApiSophonMfaRequiredError) {
      return true;
    }

    if (!(error instanceof ApiError)) {
      return false;
    }

    const statusCode = error.statusCode ?? HTTP_STATUS.STATUS_CODE.INTERNAL_SERVER_ERROR;
    return errorMap.get(statusCode)?.redirect ?? false;
  };

  return {
    handleError,
    isAuthError,
  };
}
